import { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { FileSpreadsheet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const thisMonth = () => new Date().toISOString().slice(0, 7);

export const CommissionReport = () => {
  const [month, setMonth] = useState(thisMonth());
  const [rep, setRep] = useState(null);
  useEffect(() => { api.get(`/admin/referrals/commission-report?month=${month}`).then(({ data }) => setRep(data)); }, [month]);
  const exportXlsx = async () => {
    const { data } = await api.get(`/admin/referrals/commission-report/export?month=${month}`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = `Safarshala_Commission_${month}.xlsx`; a.click(); URL.revokeObjectURL(a.href);
  };
  if (!rep) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const t = rep.totals;
  return (
    <div className="space-y-5" data-testid="commission-report">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="text-xs text-muted-foreground">Month</label><Input type="month" value={month} max={thisMonth()} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-44" data-testid="commission-month" /></div>
        <Button variant="outline" className="rounded-full ml-auto" onClick={exportXlsx} data-testid="commission-export"><FileSpreadsheet className="h-4 w-4 mr-1" /> Export Excel</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Completed trips", t.trips], ["Commission earned", inr(t.earned), "text-emerald-600"], ["Paid out this month", inr(t.paid)], ["Payout due (all time)", inr(t.due), "text-primary"]].map(([l, v, c]) => (
          <div key={l} className="rounded-xl border bg-card p-4"><p className="text-xs text-muted-foreground">{l}</p><p className={`font-heading text-2xl mt-1 ${c || ""}`} data-testid={`commission-total-${l.split(" ")[0].toLowerCase()}`}>{v}</p></div>))}
      </div>
      <div className="rounded-xl border bg-card p-4">
        <p className="text-sm font-medium mb-2">Commission earned — last 6 months</p>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={rep.trend}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="month" fontSize={11} /><YAxis fontSize={11} /><Tooltip formatter={(v) => inr(v)} />
            <Bar dataKey="earned" name="Commission" fill="#F5C518" radius={[4, 4, 0, 0]} /></BarChart>
        </ResponsiveContainer>
      </div>
      <div className="rounded-xl border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]" data-testid="commission-table">
          <thead className="bg-muted/50 text-left"><tr>{["Code", "Referrer", "Rule", "Trips", "Earned", "Paid out", "Payout due"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
          <tbody>
            {rep.rows.map((r) => (
              <tr key={r.id} className="border-t hover:bg-muted/30" data-testid="commission-row">
                <td className="p-3 font-mono font-semibold text-primary">{r.code}{!r.approved && <Badge variant="outline" className="ml-2 rounded-full text-[10px] bg-amber-500/15 text-amber-700">Pending</Badge>}</td>
                <td className="p-3"><p>{r.referrer}</p><p className="text-[11px] text-muted-foreground">{r.whatsapp}</p></td>
                <td className="p-3 text-xs">{r.rule}</td><td className="p-3">{r.trips}</td>
                <td className="p-3 font-mono">{inr(r.earned)}</td><td className="p-3 font-mono text-muted-foreground">{inr(r.paid)}</td>
                <td className={`p-3 font-mono font-semibold ${r.due > 0 ? "text-primary" : ""}`}>{inr(r.due)}</td>
              </tr>))}
            {rep.rows.length > 0 && <tr className="border-t bg-muted/30 font-semibold"><td className="p-3" colSpan={3}>Total</td><td className="p-3">{t.trips}</td><td className="p-3 font-mono">{inr(t.earned)}</td><td className="p-3 font-mono">{inr(t.paid)}</td><td className="p-3 font-mono text-primary">{inr(t.due)}</td></tr>}
            {!rep.rows.length && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">No referral codes yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
